/**
 * Dashboard Configuration
 * Multi-tier widget registry with category-based drag constraints
 *
 * Tiers:
 * 1. Stats (hero stats bar) - horizontal drag only within row
 * 2. Charts (trend charts) - horizontal drag only within row
 * 3. Widgets (feature widgets + insights) - full grid drag
 */

// ==========================================
// TIER 1: STATS REGISTRY (Hero Stats Bar)
// ==========================================
export const STATS_REGISTRY = {
  todayAppointments: {
    id: 'todayAppointments',
    label: "Today's Appointments",
    icon: 'fa fa-calendar-check-o',
    color: 'blue',
    urgency: 'info',
    clickable: true,
    defaultVisible: true
  },
  tasksToday: {
    id: 'tasksToday',
    label: 'Tasks Due Today',
    icon: 'fa fa-check-square-o',
    color: 'orange',
    urgency: 'warning',
    clickable: true,
    defaultVisible: true
  },
  unreadMessages: {
    id: 'unreadMessages',
    label: 'Unread Messages',
    icon: 'fa fa-comment-o',
    color: 'teal',
    urgency: 'info',
    clickable: true,
    defaultVisible: true
  },
  newForms: {
    id: 'newForms',
    label: 'Form Submissions',
    icon: 'fa fa-file-text-o',
    color: 'green',
    urgency: 'success',
    clickable: true,
    defaultVisible: true
  },
  overdueRecall: {
    id: 'overdueRecall',
    label: 'Overdue Recall',
    icon: 'fa fa-clock-o',
    color: 'red',
    urgency: 'urgent',
    clickable: true,
    defaultVisible: true
  }
};

// Default stats order
export const DEFAULT_STATS_ORDER = [
  'todayAppointments',
  'tasksToday',
  'unreadMessages',
  'newForms',
  'overdueRecall'
];

// ==========================================
// TIER 2: CHARTS REGISTRY (Trend Charts)
// ==========================================
export const CHARTS_REGISTRY = {
  weeklyActivity: {
    id: 'weeklyActivity',
    component: 'TrendChart',
    name: 'Weekly Activity',
    icon: 'fa fa-line-chart',
    permission: null,
    defaultVisible: true,
    isPaid: false
  },
  appointmentsChart: {
    id: 'appointmentsChart',
    component: 'TrendChart',
    name: 'Appointments Trend',
    icon: 'fa fa-calendar',
    permission: null,
    defaultVisible: true,
    isPaid: false
  },
  campaignsChart: {
    id: 'campaignsChart',
    component: 'CampaignsTrendChart',
    name: 'Campaigns',
    icon: 'fa fa-bullhorn',
    permission: 'campaigns',
    defaultVisible: true,
    isPaid: true // Campaigns is a paid feature
  }
};

// Default charts order
export const DEFAULT_CHARTS_ORDER = [
  'weeklyActivity',
  'appointmentsChart',
  'campaignsChart'
];

// ==========================================
// TIER 3: WIDGETS REGISTRY (Feature Widgets)
// ==========================================
export const WIDGET_REGISTRY = {
  // === TASKS & COMMUNICATION WIDGETS ===
  myTasks: {
    id: 'myTasks',
    component: 'MyTasksWidget',
    name: 'My Tasks Today',
    icon: 'fa fa-check-square-o',
    permission: null, // always visible
    category: 'core',
    defaultVisible: true,
    isPaid: false
  },
  unreadMessages: {
    id: 'unreadMessages',
    component: 'UnreadMessagesWidget',
    name: 'Unread Messages',
    icon: 'fa fa-comments',
    permission: null, // visibility determined by messagingFeatureEnabled
    category: 'core',
    defaultVisible: true,
    isPaid: true // Shows FeaturePreview when messaging not enabled
  },
  newForms: {
    id: 'newForms',
    component: 'NewFormsWidget',
    name: 'New Form Submissions',
    icon: 'fa fa-file-text-o',
    permission: null, // visibility determined by formsPaidFor
    category: 'core',
    defaultVisible: true,
    isPaid: true // Shows FeaturePreview when forms not paid for
  },
  // === FEATURE WIDGETS ===
  // Note: Campaigns moved to Tier 2 (Charts) as CampaignsTrendChart with embedded details
  workflow: {
    id: 'workflow',
    component: 'WorkflowWidget',
    name: 'Workflows',
    icon: 'fa fa-random',
    permission: 'workflow',
    category: 'features',
    defaultVisible: true,
    isPaid: false // Free for everyone
  },
  teamChat: {
    id: 'teamChat',
    component: 'TeamChatWidget',
    name: 'Team Chat',
    icon: 'fa fa-users',
    permission: null, // always visible
    category: 'features',
    defaultVisible: true,
    isPaid: false // Free for everyone
  },
  wiki: {
    id: 'wiki',
    component: 'WikiWidget',
    name: 'Wiki',
    icon: 'fa fa-book',
    permission: 'wiki',
    category: 'features',
    defaultVisible: true,
    isPaid: false // Free for everyone
  },
  kanban: {
    id: 'kanban',
    component: 'KanbanWidget',
    name: 'Kanban Boards',
    icon: 'fa fa-columns',
    permission: 'kanban',
    category: 'features',
    defaultVisible: true,
    isPaid: false // Free for everyone
  },
  // === INSIGHTS WIDGET ===
  insights: {
    id: 'insights',
    component: 'ActivityFeed',
    name: 'Intelligent Insights',
    icon: 'fa fa-bolt',
    permission: null, // always visible
    category: 'insights',
    defaultVisible: true,
    isPaid: false
  }
};

// Size configurations with CSS variable values
export const SIZE_CONFIG = {
  compact: {
    label: 'Compact',
    icon: 'fa fa-compress',
    columns: 4, // 4 columns for compact - higher density
    vars: {
      '--widget-stat-size': '1.125rem',
      '--widget-stat-padding': '6px 14px',
      '--widget-list-padding': '6px 14px 4px 14px',
      '--widget-list-item-padding': '8px 14px',
      '--widget-header-padding': '10px 14px',
      '--widget-max-items': '2',
      '--widget-gap': '12px',
      '--widget-columns': '4'
    }
  },
  normal: {
    label: 'Normal',
    icon: 'fa fa-square-o',
    columns: 3, // 3 columns for normal - balanced
    vars: {
      '--widget-stat-size': '1.5rem',
      '--widget-stat-padding': '12px 20px',
      '--widget-list-padding': '12px 20px 8px 20px',
      '--widget-list-item-padding': '14px 20px',
      '--widget-header-padding': '16px 20px',
      '--widget-max-items': '3',
      '--widget-gap': '20px',
      '--widget-columns': '3'
    }
  },
  large: {
    label: 'Large',
    icon: 'fa fa-expand',
    columns: 2, // 2 columns for large - more breathing room
    vars: {
      '--widget-stat-size': '1.75rem',
      '--widget-stat-padding': '16px 24px',
      '--widget-list-padding': '16px 24px 10px 24px',
      '--widget-list-item-padding': '16px 24px',
      '--widget-header-padding': '20px 24px',
      '--widget-max-items': '5',
      '--widget-gap': '28px',
      '--widget-columns': '2'
    }
  }
};

// Default widget order (left-to-right, top-to-bottom) for TIER 3 only
// Charts are now in TIER 2 (separate draggable row)
// Note: Campaigns moved to Tier 2 as CampaignsTrendChart with embedded details
// Row 1: Core widgets (My Tasks, Forms, Messages)
// Row 2: Features (Team Chat, Kanban, Wiki, Workflows)
// Row 3: Insights (full width at bottom)
const DEFAULT_WIDGET_ORDER = [
  'myTasks',
  'newForms',
  'unreadMessages',
  'teamChat',
  'kanban',
  'wiki',
  'workflow',
  'insights'
];

// Default layout configuration (multi-tier)
export const DEFAULT_LAYOUT = {
  version: 6, // Bumped: Campaigns moved back to Tier 2 as CampaignsTrendChart with embedded details
  size: 'normal',
  // Tier 1: Stats
  stats: DEFAULT_STATS_ORDER.map((id, index) => ({
    id,
    visible: STATS_REGISTRY[id].defaultVisible,
    order: index
  })),
  // Tier 2: Charts
  charts: DEFAULT_CHARTS_ORDER.map((id, index) => ({
    id,
    visible: CHARTS_REGISTRY[id].defaultVisible,
    order: index
  })),
  // Tier 3: Widgets
  widgets: DEFAULT_WIDGET_ORDER.map((id, index) => ({
    id,
    visible: WIDGET_REGISTRY[id].defaultVisible,
    order: index
  }))
};

// Storage key generator
export function getStorageKey(siteId, userId) {
  return `dashboard_layout_${siteId}_${userId}`;
}

// Save layout to localStorage with fallback
export function saveLayout(siteId, userId, layout) {
  const key = getStorageKey(siteId, userId);
  const data = JSON.stringify({
    ...layout,
    version: DEFAULT_LAYOUT.version,
    updatedAt: new Date().toISOString()
  });

  try {
    localStorage.setItem(key, data);
    return { success: true, storage: 'localStorage' };
  } catch (e) {
    // localStorage quota exceeded, try sessionStorage
    console.warn('localStorage quota exceeded, falling back to sessionStorage');
    try {
      sessionStorage.setItem(key, data);
      return { success: true, storage: 'sessionStorage' };
    } catch (e2) {
      console.error('Failed to save dashboard layout:', e2);
      return { success: false, error: e2.message };
    }
  }
}

// Load layout from storage
export function loadLayout(siteId, userId) {
  const key = getStorageKey(siteId, userId);

  // Try localStorage first
  let data = localStorage.getItem(key);

  // Fall back to sessionStorage
  if (!data) {
    data = sessionStorage.getItem(key);
  }

  if (!data) {
    return null;
  }

  try {
    const layout = JSON.parse(data);

    // Validate and migrate if needed
    if (layout.version !== DEFAULT_LAYOUT.version) {
      return migrateLayout(layout);
    }

    return layout;
  } catch (e) {
    console.error('Failed to parse dashboard layout:', e);
    return null;
  }
}

// Migrate old layout versions to current
function migrateLayout(oldLayout) {
  // For now, just return default if version mismatch
  // In future, add migration logic here
  console.log('Migrating dashboard layout from version', oldLayout.version);
  // Deep clone to avoid reference issues
  const newLayout = JSON.parse(JSON.stringify(DEFAULT_LAYOUT));
  newLayout.size = oldLayout.size || 'normal';
  return newLayout;
}

// Clear saved layout
export function clearLayout(siteId, userId) {
  const key = getStorageKey(siteId, userId);
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

// Get visible stats from layout
export function getVisibleStats(layout) {
  if (!layout || !layout.stats) {
    return DEFAULT_STATS_ORDER.map((id, index) => ({ id, visible: true, order: index }));
  }

  return layout.stats
    .filter(s => s.visible)
    .sort((a, b) => a.order - b.order);
}

// Get visible charts from layout, respecting permissions
export function getVisibleCharts(layout, canViewPermission) {
  if (!layout || !layout.charts) {
    return DEFAULT_CHARTS_ORDER.map((id, index) => ({ id, visible: true, order: index }));
  }

  return layout.charts
    .filter(c => {
      if (!c.visible) return false;
      const config = CHARTS_REGISTRY[c.id];
      if (!config) return false;
      // For paid features, always show (will show FeaturePreview if no access)
      if (config.isPaid) return true;
      if (config.permission === null) return true;
      return canViewPermission(config.permission) || canViewPermission('all');
    })
    .sort((a, b) => a.order - b.order);
}

// Get visible widgets from layout, respecting permissions
export function getVisibleWidgets(layout, canViewPermission) {
  if (!layout || !layout.widgets) {
    return [];
  }

  return layout.widgets
    .filter(w => {
      if (!w.visible) return false;
      const config = WIDGET_REGISTRY[w.id];
      if (!config) return false;
      // Check permission
      if (config.permission === null) return true;
      return canViewPermission(config.permission) || canViewPermission('all');
    })
    .sort((a, b) => a.order - b.order);
}

// Get hidden widgets (for "Add Widget" dropdown)
export function getHiddenWidgets(layout, canViewPermission) {
  if (!layout || !layout.widgets) {
    return Object.values(WIDGET_REGISTRY);
  }

  const visibleIds = new Set(
    layout.widgets.filter(w => w.visible).map(w => w.id)
  );

  return Object.values(WIDGET_REGISTRY)
    .filter(config => {
      if (visibleIds.has(config.id)) return false;
      // Check permission
      if (config.permission === null) return true;
      return canViewPermission(config.permission) || canViewPermission('all');
    });
}

// Get hidden charts (for "Add Widget" dropdown - Tier 2)
export function getHiddenCharts(layout, canViewPermission) {
  if (!layout || !layout.charts) {
    return [];
  }

  const visibleIds = new Set(
    layout.charts.filter(c => c.visible).map(c => c.id)
  );

  return Object.values(CHARTS_REGISTRY)
    .filter(config => {
      if (visibleIds.has(config.id)) return false;
      // Check permission
      if (config.permission === null) return true;
      return canViewPermission(config.permission) || canViewPermission('all');
    });
}

// Max items based on size
export function getMaxItemsForSize(size) {
  return parseInt(SIZE_CONFIG[size]?.vars['--widget-max-items'] || '3');
}
